import { useState } from 'react';
import { Alert } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowDown } from '@fortawesome/free-solid-svg-icons';
import { CallDrawer, DataTable, GhostBtn, PageHeader } from '../../components';
import { useCallLog } from '../../queries/calls';
import { useAssistantName } from '../../queries/agents';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useHomeScope } from '../../hooks/useHomeScope';
import { usePhone } from '../../hooks/usePhone';
import { errorText } from '../../utils/errors';
import { downloadCsv } from '../../utils/csv';
import CallCard from './CallCard';
import CallsToolbar from './CallsToolbar';
import { columns, csvColumns } from './columns';

const plural = (n) => `${n} ${n === 1 ? 'call' : 'calls'}`;

export default function Calls() {
  const { params, active, win } = useHomeScope();
  const assistantName = useAssistantName();
  const phone = usePhone();
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');
  const [range, setRange] = useState(active ? win.key : '30');
  const [direction, setDirection] = useState('all');
  const [handledBy, setHandledBy] = useState('all');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [selectedId, setSelectedId] = useState(null);

  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const { rows, total, isLoading, error } = useCallLog({
    type,
    range,
    scope: params.scope,
    search: debouncedSearch,
    direction,
    handledBy,
    page,
    pageSize: size
  });

  const resetPage = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const countLabel = plural(total);

  return (
    <div>
      <PageHeader title="Call Logs" subtitle="Full inbound call history, filterable by type and date range." />

      <CallsToolbar
        type={type}
        onType={resetPage(setType)}
        search={search}
        onSearch={resetPage(setSearch)}
        range={range}
        onRange={resetPage(setRange)}
        direction={direction}
        onDirection={resetPage(setDirection)}
        handledBy={handledBy}
        onHandledBy={resetPage(setHandledBy)}
        assistantName={assistantName}
      />

      {error && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={errorText(error, 'Could not load calls')} />
      )}

      <DataTable
        title={phone ? undefined : 'Calls'}
        note={isLoading ? 'Loading…' : countLabel}
        actions={
          <GhostBtn
            type="button"
            aria-label="Export CSV"
            title="Export CSV"
            style={phone ? { width: 44, height: 44, padding: 0, justifyContent: 'center' } : undefined}
            disabled={!rows.length}
            onClick={() => downloadCsv(`call-log-${range}.csv`, csvColumns(assistantName), rows)}
          >
            <FontAwesomeIcon icon={faFileArrowDown} />
            {!phone && 'Export CSV'}
          </GhostBtn>
        }
        columns={columns(assistantName)}
        phoneCard={(r) => <CallCard call={r} assistantName={assistantName} />}
        data={rows}
        loading={isLoading}
        minWidth={1240}
        selectedKey={selectedId}
        onRowClick={(r) => setSelectedId(r.id)}
        rowLabel={(r) => `Open call from ${r.name}, ${r.date} ${r.time}`}
        emptyText="No calls match these filters. Try widening the date range."
        pagination={{ page, pageSize: size, total, onChange: (p, s) => { setPage(p); setSize(s); } }}
      />

      <CallDrawer callId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
